import React, { useCallback } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { Button, Modal } from '@leagueplatform/genesis-core';
import { NavLinkInterface } from 'models/nav-config.model';
import { useMobileNavLogic } from 'hooks/use-mobile-nav-logic.hook';
import { useUserDropdownConfig } from 'hooks/use-user-dropdown-config.hook';
import { useProductSegmentation } from 'hooks/use-product-segmentation.hook';
import { sendUserOptionEvent } from 'utils/analytics-events';
import { MobileNav } from './mobile-nav.component';

interface MobileNavWrapperProps {
  navLinks: NavLinkInterface[];
  menuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
  handleDeleteAccount: () => void;
}

function useFilterUserDropdownConfig(config: any[], isMobile: boolean): any[] {
  const { formatMessage } = useIntl();
  const {
    showPDPPremium,
    showSupplementalAutoPay,
    showMedicarePremium,
    showPharmacySettings,
    showFormsCenter,
  } = useProductSegmentation();

  if (isMobile) {
    // Filter out "Privacy and Legal Documents" section for mobile
    // Also filter out privacy policy and terms of use from other sections
    const excludedItems = [
      formatMessage({ id: 'STR_PRIVACY_POLICY' }),
      formatMessage({ id: 'STR_TERMS_OF_USE' }),
    ];

    return config
      .filter(
        (section) =>
          section.heading !==
          formatMessage({ id: 'STR_PRIVACY_AND_LEGAL_DOCUMENTS' }),
      )
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item: any) => !excludedItems.includes(item.text),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }

  // Build dynamic allowed items based on product segmentation
  const allowedItems = [
    formatMessage({ id: 'STR_PERSONAL_INFO' }),
    formatMessage({ id: 'STR_SECURITY_SETTINGS' }),
    formatMessage({ id: 'STR_CUSTOMER_SUPPORT' }),
    formatMessage({ id: 'STR_ABOUT_CIGNA_HEALTHCARE' }),
    formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' }),
    formatMessage({ id: 'STR_HIPPA_NOTICE_OF_PRIVACY_PRACTICES' }),
    formatMessage({ id: 'STR_GRAMM_LEACH_BILLEY_ACT_NOTICE' }),
    formatMessage({ id: 'STR_NONDISCRIMINATION_NOTICE' }),
    formatMessage({ id: 'STR_WASHINGTON_CONSUMER_PRIVACY_NOTICE' }),
    formatMessage({ id: 'STR_APPEALS' }),
    formatMessage({ id: 'STR_GRIEVANCES' }),
    formatMessage({ id: 'STR_DISCLAIMERS' }),
    formatMessage({ id: 'STR_LANGUAGE_ASSISTANCE' }),
    formatMessage({ id: 'STR_LOG_OUT' }),
  ];

  // Add pharmacy settings conditionally based on product segmentation
  if (showPharmacySettings) {
    allowedItems.push(formatMessage({ id: 'STR_MANAGE_PHARMACY_SETTINGS' }));
  }

  // Add forms center conditionally based on product segmentation
  if (showFormsCenter) {
    allowedItems.push(formatMessage({ id: 'STR_FORMS_CENTER' }));
  }

  // Add payment links conditionally based on product segmentation
  if (showPDPPremium) {
    allowedItems.push(formatMessage({ id: 'STR_PDP_PREMIUM_PAYMENTS' }));
  }
  if (showSupplementalAutoPay) {
    allowedItems.push(
      formatMessage({ id: 'STR_SUPPLEMENTAL_PREMIUM_PAYMENT' }),
    );
  }
  if (showMedicarePremium) {
    allowedItems.push(formatMessage({ id: 'STR_MEDICARE_PREMIUM_PAYMENT' }));
  }

  return config
    .map((section) => ({
      ...section,
      items: section.items.filter((item: any) => {
        // Exclude privacy policy and terms of use from mobile
        const excludedItems = [
          formatMessage({ id: 'STR_PRIVACY_POLICY' }),
          formatMessage({ id: 'STR_TERMS_OF_USE' }),
        ];
        return (
          !excludedItems.includes(item.text) && allowedItems.includes(item.text)
        );
      }),
    }))
    .filter((section) => section.items.length > 0);
}

export const MobileNavWrapper = React.memo(
  ({
    navLinks,
    menuOpen,
    toggleMenu,
    closeMenu,
    handleDeleteAccount,
  }: MobileNavWrapperProps) => {
    const { formatMessage } = useIntl();
    const { mainNavLinksData, handleCapabilityNavLinkClick } =
      useMobileNavLogic(navLinks, closeMenu);
    const userDropdownConfig = useUserDropdownConfig(handleDeleteAccount);

    // Apply filtering logic for mobile
    const dropdownItems = useFilterUserDropdownConfig(userDropdownConfig, true);

    const handleDropdownItemClick = useCallback(
      (text: string, onClick?: () => void) => {
        closeMenu();
        sendUserOptionEvent(text);
        if (onClick) onClick();
      },
      [closeMenu],
    );

    return (
      <Modal.Root open={menuOpen} onOpenChange={toggleMenu}>
        <Modal.Trigger>
          <Button
            icon="interfaceMenu"
            hideLabel
            quiet
            priority="tertiary"
            size="large"
            css={{ margin: '$half $quarter' }}
            aria-controls={undefined}
          >
            {formatMessage({ id: 'STR_OPEN_MENU' })}
          </Button>
        </Modal.Trigger>
        <Modal.Content
          layout="right"
          css={{
            '.GDS-modal': { maxWidth: 335 },
            '.GDS-modal-content': { paddingLeft: '$one' },
          }}
        >
          <MobileNav
            mainNavLinksData={mainNavLinksData}
            dropdownItems={dropdownItems}
            onCapabilityNavLinkClick={handleCapabilityNavLinkClick}
            onDropdownItemClick={handleDropdownItemClick}
          />
        </Modal.Content>
      </Modal.Root>
    );
  },
);

MobileNavWrapper.displayName = 'MobileNavWrapper';
